import {FormattedMessage, useIntl} from "react-intl";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import '../scss/SurveyHeader.scss';
import {Link, Prompt, useHistory, useLocation, useParams} from "react-router-dom";
import {ReactComponent as BackArrow} from "../svg/back.svg";
import {Modal, Popup, PopupElement, PopupProvider, StatusText, Toast, usePopup} from "../UIElements";
import {
    EditorFocusContext,
    useOriginalSurveyData,
    useOriginalUserSurveyData,
    useSurveyData,
    useUserSurveyData
} from "./SurveyView";
import {
    GetSurveyWithId,
    GetUserBySurvey,
    PostSurveyWithId,
    PostUsersToAnnieUserSurvey,
    UpdateSurveyWithId, UpdateSurveyWithIdAndUpdateContext
} from "../api/APISurvey";
import {replaceInstructionVariables, updateTimeToServerFormat} from "../Formats";
import _, {isPlainObject} from "lodash";
import {ReactComponent as LoaderIcon} from "../svg/loader.svg";
import {ReactComponent as RedoIcon} from "../svg/redo.svg";
import {ReactComponent as UndoIcon} from "../svg/undo.svg";
import {ReactComponent as TestIcon} from "../svg/test.svg";
import {useAuthData, useCurrentUserData, useLanguageData} from "../App";
import {SurveySummary} from "./SurveySummary";
import {useSnackbar} from "../Content";
import {CopyPopup} from "./Surveys";
import {useDataContext} from "./SurveyContextProvider";
import {
    clearValueFromSessionStorage, flattenConfigObject,
    logSessionStorage,
    reconstructConfigObject,
    removeUnnecessaryKeys
} from "../DataFunctions";
import {TestSurveyModal} from "./TestSurvey";
import {GetAssistant, PostAssistant, PostAssistantWithId} from "../api/APIBot";
import {useAssistantOperations} from "../utils/assistantUtils";

function useSurveySave() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const { value: originalSurveyData, setValue: setOriginalSurveyData } = useDataContext('originalSurveyData');
    const { value: originalUserSurveyData, setValue: setOriginalUserSurveyData } = useDataContext('originalUserSurveyData');

    const { authData } = useAuthData();
    const { setSnackbarData } = useSnackbar();
    const intl = useIntl();

    const surveyUpdate = UpdateSurveyWithIdAndUpdateContext(surveyData.id, setSurveyData, setOriginalSurveyData);
    const newID = useRef(new Date().getTime().toString());
    const surveyPost = PostSurveyWithId(newID.current);
    const annieUserSurveyPost = PostUsersToAnnieUserSurvey();
    const assistantId = surveyData?.config?.assistantid || "";
    const postAssistantWithId = PostAssistantWithId(assistantId);
    const postAssistant = PostAssistant();
    const getAssistant = GetAssistant(assistantId, surveyData?.config?.type === "SUPPORTBOT");

    // Use the shared hook for assistant operations
    const { buildAssistantInstructions, fetchCombinedToolsAndResources } = useAssistantOperations();

    const clearSessionStorage = (newId) => {
        clearValueFromSessionStorage('surveyData', surveyData.id);
        clearValueFromSessionStorage('userSurveyData', surveyData.id);
        clearValueFromSessionStorage('surveyData', 'new');
        clearValueFromSessionStorage('userSurveyData', 'new');
        if (newId) {
            clearValueFromSessionStorage('surveyData', newId);
            clearValueFromSessionStorage('userSurveyData', newId);
        }
    };

    const handleSaveError = (error) => {
        setSnackbarData({
            show: true,
            text: intl.formatMessage({
                id: 'toast.saveFailed',
                defaultMessage: 'The save failed. Please try again.',
            }),
            state: "error",
            autoHide: true,
        });
    };

    const handleSaveSuccess = (callback) => {
        setSnackbarData({
            show: true,
            text: intl.formatMessage({
                id: 'toast.saveSuccess',
                defaultMessage: 'Survey Saved!',
            }),
            state: "success",
            autoHide: true,
        });
        if (callback) callback();
    };

    const processSurveyUpdate = (isNewSurvey, isDraft, callback, cleanedSurveyData) => {
        if (isNewSurvey) {
            cleanedSurveyData.id = newID.current;
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData);

            surveyPost.mutate(surveyDataForPost, {
                onError: handleSaveError,
                onSuccess: (response) => {
                    if (userSurveyData.length < 1) {
                        clearSessionStorage();
                        if (callback) {
                            callback(newID.current);
                        }
                    } else {
                        const newAnnieUserSurveyData = [...userSurveyData];
                        newAnnieUserSurveyData.forEach(annieuser => annieuser.survey = newID.current);
                        annieUserSurveyPost.mutate(newAnnieUserSurveyData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                clearSessionStorage();
                                if (callback) callback(newID.current);
                            }
                        });
                    }
                }
            });
        } else {
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData);

            surveyUpdate.mutate(surveyDataForPost, {
                onError: handleSaveError,
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        setOriginalUserSurveyData(_.cloneDeep(userSurveyData));
                        clearSessionStorage();
                        handleSaveSuccess(callback);
                    } else {
                        annieUserSurveyPost.mutate(userSurveyData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                setOriginalUserSurveyData(_.cloneDeep(userSurveyData));
                                clearSessionStorage();
                                handleSaveSuccess(callback);
                            }
                        });
                    }
                }
            });
        }
    };

    const saveSurvey = async (isNewSurvey, isDraft, callback) => {
        const newSurveyData = {...surveyData};
        const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData);

        cleanedSurveyData.updated = updateTimeToServerFormat(new Date());
        cleanedSurveyData.updatedby = authData.uid;

        if (isDraft) {
            cleanedSurveyData.status = "DRAFT";
        } else if (cleanedSurveyData.status === "DRAFT") {
            cleanedSurveyData.status = "SCHEDULED";
        }

        setSurveyData(cleanedSurveyData);

        if (surveyData?.config?.type === "SUPPORTBOT") {
            // Build assistant instructions using shared function
            const surveyId = surveyData?.id === "new" ? newID.current : surveyData?.id;
            const processedAssistantData = buildAssistantInstructions(surveyData, surveyId, false);

            // Fetch and combine tools/resources from all support bots
            try {
                const { tools, tool_resources } =
                    await fetchCombinedToolsAndResources(surveyData?.config?.supportBots);

                if (tools) {
                    processedAssistantData.tools = tools;
                }
                if (tool_resources) {
                    processedAssistantData.tool_resources = tool_resources;
                }
            } catch (error) {
                console.error('Error fetching assistant tools:', error);
                // Continue without tools if there's an error
            }

            if (assistantId) {
                if (assistantId) {
                    if (getAssistant.isLoading) {
                        return;
                    } else if (getAssistant.isSuccess && getAssistant.data) {
                        // PRESERVE existing tool_resources if they exist, otherwise use fetched ones
                        if (getAssistant.data.tool_resources && !processedAssistantData.tool_resources) {
                            processedAssistantData.tool_resources = getAssistant.data.tool_resources;
                        } else if (getAssistant.data.tool_resources?.file_search?.vector_store_ids) {
                            // If both exist, prefer the existing one
                            if (!processedAssistantData.tool_resources) {
                                processedAssistantData.tool_resources = {};
                            }
                            processedAssistantData.tool_resources.file_search = getAssistant.data.tool_resources.file_search;
                        }

                        // PRESERVE existing tools if they exist, otherwise use fetched ones
                        if (getAssistant.data.tools && !processedAssistantData.tools) {
                            processedAssistantData.tools = getAssistant.data.tools;
                        }

                        // Update the existing assistant
                        postAssistantWithId.mutate(processedAssistantData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                            }
                        });
                    } else if (getAssistant.isError) {
                        handleSaveError();
                    }
                }
            } else {
                // Create a new assistant
                postAssistant.mutate(processedAssistantData, {
                    onError: handleSaveError,
                    onSuccess: (response) => {
                        if (response && response?.data?.id) {
                            cleanedSurveyData.config.assistantid = response.data.id;
                            setSurveyData(cleanedSurveyData);
                        }
                        processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                    }
                });
            }
        } else {
            // Non-SUPPORTBOT surveys proceed directly
            processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
        }
    };

    return {
        saveSurvey,
        clearSessionStorage,
        isLoading: surveyUpdate.isLoading || surveyPost.isLoading ||
            postAssistantWithId.isLoading || postAssistant.isLoading
    };
}

function SurveyOverviewModal({ isOpen, onClose, onConfirm, confirmText, isLoading, showSkipChecksButton = false}) {
    const [surveyChecked, setSurveyChecked] = useState(false);
    const intl = useIntl();
    const { currentUserData } = useCurrentUserData();
    const userSuperAdmin = currentUserData.superuser && currentUserData.id.endsWith("@annieadvisor.com");

    if (!isOpen) return null;

    const savingText = intl.formatMessage({
        id: 'saving',
        defaultMessage: 'Saving...'
    })

    return (
        <Modal
            closeModal={onClose}
            header={intl.formatMessage({
                id: 'survey.overview',
                defaultMessage: 'Survey Overview',
            })}
            confirmAction={onConfirm}
            confirmText={isLoading ? savingText : confirmText}
            scrollContent={true}
            confirmDisabled={!surveyChecked || isLoading}
            disabledText={isLoading ? "" :
                <>
                    {intl.formatMessage({
                        id: 'survey.overview.checkErrors',
                        defaultMessage: 'Check errors before publishing'
                    })}
                    {showSkipChecksButton && userSuperAdmin &&
                        <button className={"override"} onClick={() => setSurveyChecked(true)}>
                            {intl.formatMessage({
                                id: 'survey.overview.bypassTests',
                                defaultMessage: 'Skip checks',
                            })}
                        </button>
                    }
                </>
            }
        >
            <SurveySummary setSurveyChecked={surveyStatus => setSurveyChecked(surveyStatus)}/>
        </Modal>
    );
}

export default function SurveyHeader() {
    const { value: surveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const { value: originalSurveyData } = useDataContext('originalSurveyData');
    const { value: originalUserSurveyData } = useDataContext('originalUserSurveyData');

    return (
        <PopupProvider>
            <HeaderRender
                surveyData={surveyData}
                originalSurveyData={originalSurveyData}
                userSurveyData={userSurveyData}
                originalUserSurveyData={originalUserSurveyData}
            />
            <PopupElement />
        </PopupProvider>
    );
}

function HeaderRender({surveyData, originalSurveyData, userSurveyData, originalUserSurveyData}) {
    const intl = useIntl();
    const history = useHistory();
    const { saveSurvey, clearSessionStorage } = useSurveySave();

    const [confirmNavigation, setConfirmNavigation] = useState(false);
    const [nextLocationPathname, setNextLocationPathname] = useState("");
    const [newSurveyPathname, setNewSurveyPathname] = useState("")
    const [navigationState, setNavigationState] = useState(null)

    // Effect to handle navigation after confirmation
    useEffect(() => {
        if (confirmNavigation && (newSurveyPathname || nextLocationPathname)) {
            clearSessionStorage();
            if (isPlainObject(navigationState)) {
                history.push({
                    pathname: newSurveyPathname || nextLocationPathname,
                    state: navigationState
                })
            } else {
                history.push(newSurveyPathname || nextLocationPathname);
            }
        }

        return () => {
            if (confirmNavigation) {
                setConfirmNavigation(false);
                setNavigationState(null)
                setNextLocationPathname("")
                setNewSurveyPathname("")
            }
        };
    }, [confirmNavigation, history, nextLocationPathname, navigationState, clearSessionStorage]);

    const surveyName = surveyData.config.title;
    let name;
    if (surveyData.id === "new" && !surveyName) {
        name = intl.formatMessage({
            id: 'survey.header.newSurvey',
            defaultMessage: 'New Survey',
        });
    } else {
        name = surveyName;
    }

    let unsavedChanges = JSON.stringify(surveyData) !== JSON.stringify(originalSurveyData) ||
        JSON.stringify(userSurveyData) !== JSON.stringify(originalUserSurveyData);

    return (
        <>
            <div className={"survey-header"}>
                <Link to="/surveys" className={"back"}>
                    <BackArrow />
                </Link>
                <h3>{name}</h3>
                <StatusText survey={surveyData}/>
                {unsavedChanges &&
                    <p className={"unsaved-changes"}>
                        {intl.formatMessage({
                            id: 'survey.header.unsavedChanges',
                            defaultMessage: 'Unsaved changes',
                        })}
                    </p>
                }
                <SaveEdits
                    setConfirmNavigation={setConfirmNavigation}
                    setNewSurveyPathname={setNewSurveyPathname}
                    setNavigationState={setNavigationState}
                />
                <PromptDialog
                    unsavedChanges={unsavedChanges}
                    confirmNavigation={confirmNavigation}
                    setConfirmNavigation={setConfirmNavigation}
                    setNewSurveyPathname={setNewSurveyPathname}
                    setNextLocationPathname={setNextLocationPathname}
                />
            </div>
        </>
    );
}

function PromptDialog({unsavedChanges, confirmNavigation, setConfirmNavigation, setNextLocationPathname }) {
    const { value: surveyData } = useDataContext('surveyData');
    const intl = useIntl();
    const { saveSurvey, isLoading } = useSurveySave();

    const [showPublishSummary, setShowPublishSummary] = useState(false);
    const [showSavePrompt, setShowSavePrompt] = useState(false)

    const surveyIsDraft = surveyData.hasOwnProperty("status") && surveyData.status === "DRAFT";
    const surveyIsDeleted = surveyData.hasOwnProperty("status") && surveyData.status === "DELETED";

    // Check if we should prompt before navigation
    const checkForNextLocation = (nextLocation) => {
        setNextLocationPathname(nextLocation.pathname);
        if (confirmNavigation) {
            return true;
        } else if (nextLocation.pathname.includes(`/support-campaign/${surveyData?.id}`)) {
            return true
        } else {
            setShowSavePrompt(true);
            return false
        }
    };

    // Handle save or open overview based on survey status
    const saveOrOpenOverview = () => {
        if (surveyIsDraft || surveyIsDeleted) {
            saveSurvey(surveyData.id === "new", true, () => {
                setConfirmNavigation(true)
            });
        } else {
            setShowSavePrompt(false);
            setShowPublishSummary(true);
        }
    };

    const handlePublish = () => {
        saveSurvey(surveyData.id === "new", false, () => {
            setConfirmNavigation(true);
        });
    }

    // Determine appropriate button text
    const modalSaveText = intl.formatMessage({
        id: 'survey.save.save',
        defaultMessage: 'Save',
    });

    const checkAndSave = intl.formatMessage({
        id: 'survey.save.checkSave',
        defaultMessage: 'Check & Save',
    });

    const savingText = intl.formatMessage({
        id: 'saving',
        defaultMessage: 'Saving...'
    })

    return (
        <>
            <Prompt
                when={unsavedChanges}
                message={checkForNextLocation}
            />

            {showSavePrompt && (
                <Modal
                    header={intl.formatMessage({
                        id: 'survey.unsavedChanges.header',
                        defaultMessage: 'Save changes to survey?',
                    })}
                    text={
                        <FormattedMessage
                            id={"survey.unsavedChanges.text"}
                            defaultMessage={"Do you wish to save changes before leaving page?{br}Any unsaved changes will be lost."}
                            values={{br: <br/>}}
                        />
                    }
                    discardText={intl.formatMessage({
                        id: 'survey.unsavedChanges.discard',
                        defaultMessage: "Don't save",
                    })}
                    confirmText={(surveyIsDraft || surveyIsDeleted) ? isLoading ? savingText : modalSaveText : checkAndSave}
                    confirmDisabled={isLoading}
                    discardAction={() => setConfirmNavigation(true)}
                    closeModal={() => setShowSavePrompt(false)}
                    confirmAction={saveOrOpenOverview}
                />
            )}

            <SurveyOverviewModal
                isOpen={showPublishSummary}
                onClose={() => setShowPublishSummary(false)}
                isLoading={isLoading}
                onConfirm={handlePublish}
                confirmText={modalSaveText}
                showSkipChecksButton={true}
            />
        </>
    );
}

function SaveEdits({setConfirmNavigation, setNavigationState, setNewSurveyPathname}) {
    const { value: surveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const intl = useIntl();
    const history = useHistory();
    const { surveyId } = useParams();
    const { saveSurvey, clearSessionStorage, isLoading } = useSurveySave();
    const { currentUserData } = useCurrentUserData();
    const { showPopup, hidePopup } = usePopup();

    const [showPublishSummary, setShowPublishSummary] = useState(false);
    const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);

    const createNewSurvey = surveyId === "new";
    const surveyIsDraft = surveyData.hasOwnProperty("status") && surveyData.status === "DRAFT";
    const surveyIsScheduled = surveyData.hasOwnProperty("status") && surveyData.status === "SCHEDULED";
    const surveyIsFinished = surveyData.hasOwnProperty("status") && surveyData.status === "FINISHED";
    const surveyIsDeleted = surveyData.hasOwnProperty("status") && surveyData.status === "DELETED";
    const userSuperAdmin = currentUserData.superuser && currentUserData.id.endsWith("@annieadvisor.com");

    // Handle location state for displaying toasts
    let location = useLocation();
    const { setSnackbarData } = useSnackbar();

    useEffect(() => {
        if (location.state?.newSavedSurvey) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'toast.saveSuccess',
                    defaultMessage: 'Survey Saved',
                }),
                state: "success",
                autoHide: true,
            });
        }

        if (location.state?.surveyDuplicated) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'toast.surveyDuplicated',
                    defaultMessage: 'Survey duplicated',
                }),
                state: "success",
                autoHide: true,
            });
        }
    }, [location.state, setSnackbarData, intl]);

    // Determine button text based on survey state
    let draftText = surveyIsDraft ?
        intl.formatMessage({
            id: 'survey.save.save',
            defaultMessage: 'Save',
        }) :
        intl.formatMessage({
            id: 'survey.save.draft',
            defaultMessage: 'Change to draft',
        });

    let saveText = (surveyIsDraft || createNewSurvey) ?
        intl.formatMessage({
            id: 'survey.save.checkPublish',
            defaultMessage: 'Check & Publish',
        }) :
        intl.formatMessage({
            id: 'survey.save.checkSave',
            defaultMessage: 'Check & Save',
        });

    let modalSaveText = (surveyIsDraft || createNewSurvey) ?
        intl.formatMessage({
            id: 'survey.save.publish',
            defaultMessage: 'Publish',
        }) :
        intl.formatMessage({
            id: 'survey.save.save',
            defaultMessage: 'Save',
        });

    // Navigate to newly created survey
    const navigateToNewIdAfterDuplication = (idForNavi) => {
        if (idForNavi) {
            setNavigationState({surveyDuplicated: true})
            setConfirmNavigation(true)
            setNewSurveyPathname(`/support-campaign/${idForNavi}`)
        }
    };

    const handleSave = () => {
        if (createNewSurvey) {
            saveSurvey(true, true, (newID) => {
                if (newID) {
                    setNavigationState({newSavedSurvey: true})
                    setConfirmNavigation(true)
                    setNewSurveyPathname(`/support-campaign/${newID}`)
                }
            })
        } else {
            saveSurvey(false, true)
        }
    }

    const handlePublish = () => {
        if (createNewSurvey) {
            saveSurvey(true, false, (newID) => {
                if (newID) {
                    setNavigationState({newSavedSurvey: true})
                    setConfirmNavigation(true)
                    setNewSurveyPathname(`/support-campaign/${newID}`)
                }
            })
        } else {
            saveSurvey(false, false, () => {
                setShowPublishSummary(false)
            })
        }
    }

    return (
        <>
            <div className={"save-toolbar"}>
                <UndoRedoComponent />
                <button
                    className={"test-survey"}
                    onClick={() => showPopup(<TestSurveyModal onClose={() => hidePopup()}/>)}
                >
                    <TestIcon />
                    {intl.formatMessage({
                        defaultMessage: 'Send a Test',
                        id: 'sendATestSurvey'
                    })}
                </button>

                {isLoading ? (
                    <>
                        <button className={"draft"} disabled>
                            <LoaderIcon/>
                        </button>
                        <button className={"save"} disabled>
                            <LoaderIcon/>
                        </button>
                    </>
                ) : (
                    <>
                        {(surveyIsScheduled || surveyIsDraft || surveyIsDeleted) && (
                            <button className={"draft"} onClick={handleSave}>
                                {draftText}
                            </button>
                        )}
                        {(surveyIsScheduled || surveyIsFinished) && (
                            <button className={"draft"} onClick={() => setShowDuplicatePopup(true)}>
                                {intl.formatMessage({
                                    id: "survey.duplicate",
                                    defaultMessage: "Duplicate"
                                })}
                            </button>
                        )}
                        {!surveyIsDeleted && (
                            <button className={"save"} onClick={() => setShowPublishSummary(true)}>
                                {saveText}
                            </button>
                        )}
                    </>
                )}
            </div>

            <SurveyOverviewModal
                isOpen={showPublishSummary}
                onClose={() => setShowPublishSummary(false)}
                isLoading={isLoading}
                onConfirm={handlePublish}
                confirmText={modalSaveText}
                showSkipChecksButton={true}
            />

            {showDuplicatePopup && (
                <CopyPopup
                    survey={reconstructConfigObject(surveyData)}
                    closePopup={() => setShowDuplicatePopup(false)}
                    refetch={() => console.log("Duplicate created")}
                    duplicateFromSurveyEditor={true}
                    setNewIdForNavigation={navigateToNewIdAfterDuplication}
                />
            )}
        </>
    );
}

function UndoRedoComponent() {
    const intl = useIntl();
    const { anyEditorFocused } = useContext(EditorFocusContext);
    const dataContext = useDataContext();
    const { undo, redo, history, future } = dataContext;

    const dataContextRef = useRef();
    dataContextRef.current = dataContext;

    const handleKeyDown = (event) => {
        if (anyEditorFocused) return;

        // Check for undo/redo key combinations
        if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
            event.preventDefault();
            if (event.shiftKey) {
                if (dataContextRef.current.future.length >= 1) {
                    dataContextRef.current.redo();
                }
            } else {
                if (dataContextRef.current.history.length >= 2) {
                    dataContextRef.current.undo();
                }
            }
        }
        // Handle Ctrl+Y for redo on Windows/Linux
        else if (event.ctrlKey && event.key === 'y') {
            event.preventDefault();
            if (dataContextRef.current.future.length >= 1) {
                dataContextRef.current.redo();
            }
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [anyEditorFocused]);

    const undoText = intl.formatMessage({id:'undo', defaultMessage:'Undo'});
    const redoText = intl.formatMessage({id:'redo', defaultMessage:'Redo'});

    return (
        <div className={"undo-redo"}>
            {history.length < 2 ? (
                <button disabled title={undoText}><UndoIcon /></button>
            ) : (
                <button title={undoText} onClick={undo}><UndoIcon /></button>
            )}

            {future.length < 1 ? (
                <button title={redoText} disabled><RedoIcon /></button>
            ) : (
                <button title={redoText} onClick={redo}><RedoIcon /></button>
            )}
        </div>
    );
}